import { Button, Grid, Tooltip, Typography } from "@material-ui/core";
import { Formik } from "formik";
import { get } from "lodash";
import React from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "../../../../../../components/Dialog";
import { CheckBox } from "../../../../../../components/inputs/BoldInput";
import {
  getFactorClient,
  updateReportVisibility
} from "../../../../../../modules/factoringClient";
import { closeDialog, getDialog, openSnackbar } from "../../../../../../modules/ui";
import {
  isReportVisible,
  REPORT_VISIBILITY_DIALOG_VARIANT,
  REPORT_VISIBILITY_TYPES
} from "./reportVisibilityTypes";

interface Props {
  factoringId: string;
  enabledContractTypes?: string[];
}

const ReportVisibilityDialog = ({
  factoringId,
  enabledContractTypes = []
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const dialog = useSelector(getDialog);
  const factoring: any = useSelector(getFactorClient);
  const open = dialog.variant === REPORT_VISIBILITY_DIALOG_VARIANT;

  const reportVisibility: Record<string, boolean> = get(
    factoring,
    "report_visibility",
    {}
  );

  const handleClose = (): void => {
    dispatch(closeDialog());
  };

  const initialValues = REPORT_VISIBILITY_TYPES.reduce(
    (acc, type) => ({
      ...acc,
      [type.value]: isReportVisible(reportVisibility, type.value)
    }),
    {} as Record<string, boolean>
  );

  return (
    <Dialog open={open} fullWidth maxWidth="sm" onClose={handleClose}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const hidden = REPORT_VISIBILITY_TYPES.reduce((acc, type) => {
              if (!values[type.value]) {
                return { ...acc, [type.value]: false };
              }
              return acc;
            }, {} as Record<string, boolean>);
            await dispatch(updateReportVisibility(factoringId, hidden));
            dispatch(openSnackbar("success", "Updated report visibility."));
            handleClose();
          } catch (error) {
            dispatch(openSnackbar("error", "Could not update report visibility."));
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ handleSubmit, values, setFieldValue, isSubmitting }): JSX.Element => (
          <>
            <DialogTitle onClose={handleClose}>Report Visibility</DialogTitle>
            <DialogContent>
              <Typography paragraph>
                Select which reports this client can see on their Reports page.
                Unchecked reports are hidden from the client and blocked from
                the report API. Clients with nothing configured see every
                report.
              </Typography>
              <Grid container direction="column">
                {REPORT_VISIBILITY_TYPES.map(type => {
                  const contractGated =
                    Boolean(type.contractType) &&
                    !enabledContractTypes.includes(type.contractType as string);
                  const checkbox = (
                    <CheckBox
                      value={values[type.value]}
                      name={type.value}
                      label={type.label}
                      setFieldValue={setFieldValue}
                      disabled={contractGated}
                    />
                  );
                  return (
                    <Grid item key={type.value}>
                      {contractGated ? (
                        <Tooltip
                          placement="left"
                          title="This client's contract type does not include this report."
                        >
                          <div>{checkbox}</div>
                        </Tooltip>
                      ) : (
                        checkbox
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={(): void => handleSubmit()}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </>
        )}
      </Formik>
    </Dialog>
  );
};

export default ReportVisibilityDialog;
